import { GlyphLoading } from "#root/components/glyphs";
import Pager from "#root/components/pager";
import { useData, useVersionedTimer } from "#root/utility/hooks";
import type RemoteCollection from "#root/utility/remote-collection";
import type { ReactNode } from "react";
import { type JSX, useCallback, useRef } from "react";
import { MdClose } from "react-icons/md";
import { Link, useSearchParams } from "react-router-dom";
import type { z, ZodType } from "zod";

export type PaginatedListProps<ListRowType extends ZodType> = {
  collection: RemoteCollection<ListRowType, any>;
  className?: string;
  title: ReactNode;
  itemsPerPage?: number;
  children: (result: { success: true; rows: z.output<ListRowType>[]; foundRows?: number | undefined }) => JSX.Element;
  allowSearch?: boolean;
  refresh?: number;
};

export default function PaginatedList<ListRowType extends ZodType>({
                                                                     className = "",
                                                                     title,
                                                                     collection,
                                                                     itemsPerPage = 100,
                                                                     children,
                                                                     allowSearch = true,
                                                                     refresh = 3600_000,
                                                                   }: PaginatedListProps<ListRowType>) {
  const version = useVersionedTimer(refresh);

  const searchElement = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageString = searchParams.get("page");
  const page = pageString ? Number.parseInt(pageString, 10) : 0;

  const q = searchParams.get("q");

  const handleCancel = useCallback(() => {
    const currentEl = searchElement.current;
    if (currentEl) {
      currentEl.value = "";
    }
  }, []);

  const handleSubmit = useCallback(
    evnt => {
      evnt.preventDefault();

      const q = searchElement.current?.value;
      if (q) {
        setSearchParams({ q });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const [result] = useData(
    async () => {
      const offset = page * itemsPerPage;

      if (q) {
        return collection.search({ search: q, offset, limit: itemsPerPage });
      }

      return collection.list({ offset, limit: itemsPerPage });
    },
    [collection, page, q ?? version],
    { persist: true },
  ); // q ?? version disables the data update when a user has run a search

  const foundRows = result?.foundRows || 0;
  const totalPages = Math.ceil(foundRows / itemsPerPage);

  return (
    <section className={`paginated-list ${className}`}>
      <h2>
        <span className="label">{title}</span>
        {allowSearch && (
          <div className="search">
            <form autoComplete="off" action="about:" onSubmit={handleSubmit}>
              <input ref={searchElement} type="text" placeholder="Search" defaultValue={q ?? ""}/>
              <Link to={{ search: "" }} onClick={handleCancel} style={q ? {} : { visibility: "hidden" }}>
                <MdClose/>
              </Link>
              <button type="submit" className="flat">Search</button>
            </form>
          </div>
        )}
        <Pager style={totalPages > 1 ? {} : { visibility: "hidden" }} page={page} totalPages={totalPages}/>
      </h2>
      {result ? children(result) : <GlyphLoading/>}
    </section>
  );
}
